/**
 * ============================================================================
 * FLEET SCHEDULE & CARTAGE COST CALCULATOR
 * ============================================================================
 * 
 * This module calculates turnaround schedules, required job days, and cartage
 * costs when allocating 1 or more trucks to a haulage job.
 * 
 * ----------------------------------------------------------------------------
 * BUSINESS RULES & LOGIC EXPLANATION
 * ----------------------------------------------------------------------------
 * 1. SINGLE TRUCK FLEET (allocatedTrucks === 1):
 *    - With 1 truck, the operation is straightforward: the truck moves back
 *      and forth between the loading site and the disposal yard.
 *    - Cycle: Load at site -> Drive to yard -> Tip -> Drive back -> Repeat.
 * 
 * 2. MULTI-TRUCK FLEET (allocatedTrucks >= 2) & LOADING SITE QUEUE:
 *    - Physical constraint: At the loading site, only ONE truck can be loaded
 *      at any given time (e.g. single excavator or loading bay).
 *    - Morning staggered start:
 *      * Truck 1 arrives at startTime (e.g. 07:00) and loads for `loadTime`
 *        (e.g. 10 mins) -> departs at 07:10.
 *      * Truck 2 CANNOT start loading until Truck 1 has finished -> starts at
 *        07:10, finishes at 07:20, departs at 07:20.
 *      * Truck 3 starts at 07:20, finishes at 07:30, departs at 07:30.
 *      * In general, Truck k starts at: startTime + (k - 1) * loadTime.
 *    - Subsequent loads during the day:
 *      * When any truck returns to the loading site, if the loading bay is
 *        currently busy loading another truck, it must wait in line (FIFO).
 *      * If the loading bay is free, it begins loading immediately.
 * 
 * 3. DISPOSAL SITE (TIPPING) UNCONSTRAINED:
 *    - Unlike the loading site, trucks do NOT queue at the tip site.
 *    - Any truck arriving at the tip site can tip immediately for `tipTime`.
 * 
 * 4. TRAVEL TIME RESOLUTION ("duration_travel"):
 *    A. When item was added WITHOUT clicking "Calculate Haulage" (Estimate):
 *       - Uses the fixed travel duration from the map polyline (`mapDurationMinutes`).
 *    B. When item was added WITH "Calculate Haulage" (Server Calculated):
 *       - Uses the dynamic `routes` data returned by `/routes/loads` API.
 *       - Specifically matches time-of-day traffic via the user's rule:
 *         * For outbound legs ("load to tip"):
 *           - If the truck leaves the loading site AT OR BEFORE the 1st
 *             "load to tip" route departure -> use the 1st route's `duration_travel`.
 *           - If the truck leaves AFTER route (k-1) but AT OR BEFORE route k ->
 *             use the k-th route's `duration_travel` (e.g. after 1st but before 2nd
 *             -> use 2nd route).
 *           - If the truck leaves AFTER the last "load to tip" route ->
 *             use the last route's `duration_travel`.
 *         * For inbound legs ("tip to load"):
 *           - Symmetrically matches the return departure from the tip site
 *             against the "tip to load" route departures.
 * 
 * 5. JOB DAYS & CARTAGE COST COMPUTATION:
 *    - `dailyFleetLoads`: Total completed loads delivered by all allocated trucks
 *      during 1 standard work day (from startTime to finishTime).
 *    - `calculatedDays`: totalLoads / dailyFleetLoads.
 *    - `fullDays`: Math.floor(calculatedDays).
 *    - `remainderDays`: 0, 0.5 (half day), or 1.0 (full day) based on standard
 *      industry billing increments.
 *    - `billedDays`: fullDays + remainderDays.
 *    - `baseCartageCost`: (allocatedTrucks * fullDays * dayRate) + ((fullDays === 0 ? allocatedTrucks : 1) * remainderDays * dayRate)
 * ============================================================================
 */

// ----------------------------------------------------------------------------
// 1. TIME UTILITIES
// ----------------------------------------------------------------------------

/**
 * Parses a time string (e.g. "07:00", "7:30", "17:00") into minutes from midnight.
 * @param {string|number} timeStr
 * @returns {number} Minutes from midnight (e.g. 07:00 -> 420)
 */
export function parseTimeToMinutes(timeStr) {
  if (timeStr == null) return 0;
  if (typeof timeStr === "number") return timeStr;
  const str = String(timeStr).trim();
  const parts = str.split(":");
  if (parts.length < 2) return 0;
  const hours = parseInt(parts[0], 10) || 0;
  const mins = parseInt(parts[1], 10) || 0;
  return hours * 60 + mins;
}

/**
 * Converts minutes from midnight into a 24-hour "HH:MM" string.
 * @param {number} minutes
 * @returns {string} E.g. 430 -> "07:10"
 */
export function minutesToTimeString(minutes) {
  if (minutes == null || isNaN(minutes)) return "00:00";
  const totalMins = Math.floor(minutes);
  const normalizedMins = ((totalMins % 1440) + 1440) % 1440;
  const h = Math.floor(normalizedMins / 60);
  const m = normalizedMins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Parses a duration string (e.g. "00:35", "35m", "1h 15m") into total minutes.
 * @param {string|number} durationStr
 * @returns {number} Duration in minutes
 */
export function parseDurationToMinutes(durationStr) {
  if (durationStr == null) return 0;
  if (typeof durationStr === "number") return Math.max(0, Math.round(durationStr));
  const str = String(durationStr).trim();
  if (str.includes(":")) {
    const parts = str.split(":");
    return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0);
  }
  const match = str.match(/(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?/i);
  if (match && (match[1] || match[2])) {
    const hours = parseInt(match[1] || 0, 10);
    const mins = parseInt(match[2] || 0, 10);
    return hours * 60 + mins;
  }
  const parsed = parseFloat(str);
  return !isNaN(parsed) ? Math.max(0, Math.round(parsed)) : 0;
}

/**
 * Formats a duration in minutes into a clean human-readable string (e.g. "35 min", "1 hr 15 min").
 * @param {number} minutes
 * @returns {string}
 */
export function formatMinutesText(minutes) {
  if (!minutes || isNaN(minutes) || minutes <= 0) return "0 min";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h} hr ${m} min`;
  if (h > 0) return `${h} hr`;
  return `${m} min`;
}

/**
 * Calculates standard full and remainder billing days for haulage:
 * - Whole days are full days.
 * - Remaining fractions <= 0.5 are billed as a half day (0.5).
 * - Remaining fractions > 0.5 are billed as a full day (1.0).
 * 
 * @param {number} days Fractional days
 * @returns {{ fullDays: number, remainderDays: number, billedDays: number }}
 */
export function getFullAndRemainderDays(days) {
  if (!days || days <= 0 || isNaN(days)) {
    return { fullDays: 0, remainderDays: 0, billedDays: 0 };
  }
  let fullDays = Math.floor(days);
  const rem = days - fullDays;
  let remainderDays = 0;
  if (rem > 0.0001) {
    if (rem <= 0.50001) {
      remainderDays = 0.5;
    } else {
      fullDays += 1;
      remainderDays = 0;
    }
  }
  return {
    fullDays,
    remainderDays,
    billedDays: fullDays + remainderDays,
  };
}

// ----------------------------------------------------------------------------
// 2. DYNAMIC ROUTE TRAVEL TIME RESOLUTION
// ----------------------------------------------------------------------------

/**
 * Derives the departure time (in minutes from midnight) when a route leg left its origin.
 * In handle_route_loads.py:
 *   completed_at = depart_at + duration_travel + tip_time
 * Therefore:
 *   depart_at = completed_at - duration_travel - duration_tip
 * 
 * @param {Object} route Route object from load.routes
 * @param {string} direction 'load to tip' | 'tip to load'
 * @param {number} fallbackStartMins Default morning start (420 = 07:00)
 * @param {number} fallbackLoadMins Default load duration (10)
 * @returns {number} Departure time in minutes from midnight
 */
export function getRouteDepartureMinutes(
  route,
  direction,
  fallbackStartMins = 420,
  fallbackLoadMins = 10
) {
  if (!route) return fallbackStartMins + fallbackLoadMins;

  if (route.depart_at != null) {
    return parseTimeToMinutes(route.depart_at);
  }
  if (route.departure_time != null) {
    return parseTimeToMinutes(route.departure_time);
  }
  if (route.completed_at != null) {
    const completedMins = parseTimeToMinutes(route.completed_at);
    const travelMins = parseDurationToMinutes(route.duration_travel);
    if (direction === "load to tip") {
      const tipMins = parseDurationToMinutes(
        route.duration_tip || route.duration_trip || 10
      );
      return completedMins - travelMins - tipMins;
    } else {
      return completedMins - travelMins;
    }
  }
  return fallbackStartMins + fallbackLoadMins;
}

/**
 * Determines the travel duration (in minutes) for a truck leaving at `departureMinutes`.
 * 
 * Implements the user's exact specification:
 * "Assume that the first leg of every day of every truck will be 'load to tip',
 *  if they leave the loading site before the first 'load to tip' route, then use
 *  the 'duration_travel' time for the first route, if they leave the site after the
 *  first 'load to tip' route but before the next 'load to tip' route then use the
 *  secound 'load to tip' record. If the leave after the last 'load to tip' record,
 *  then just use the las 'load to tip' record."
 * 
 * @param {number} departureMinutes Time in minutes from midnight when the truck departs
 * @param {string} direction 'load to tip' | 'tip to load'
 * @param {Array<Object>|null} routes Array of routes from /routes/loads API
 * @param {number} fallbackTravelMinutes Fixed map duration in minutes (used if no API routes)
 * @returns {number} Travel time in minutes for this leg
 */
export function getTravelDurationForDeparture(
  departureMinutes,
  direction = "load to tip",
  routes = null,
  fallbackTravelMinutes = 30
) {
  // If routes are not available (e.g. user did not click "Calculate Haulage"),
  // fall back to the fixed driving duration from the map.
  const dirRoutes = (routes || []).filter((r) => r && r.direction === direction);
  if (!dirRoutes || dirRoutes.length === 0) {
    return Math.max(1, Math.round(fallbackTravelMinutes) || 30);
  }

  // Pre-calculate departure times and travel minutes for all matching routes
  const preparedRoutes = dirRoutes.map((r, idx) => ({
    route: r,
    departureMinutes: getRouteDepartureMinutes(r, direction),
    durationTravelMinutes:
      parseDurationToMinutes(r.duration_travel) || fallbackTravelMinutes || 30,
    index: idx,
  }));

  // Rule 1: If truck leaves at or before the first route, use the 1st route duration
  const firstRoute = preparedRoutes[0];
  if (departureMinutes <= firstRoute.departureMinutes) {
    return firstRoute.durationTravelMinutes;
  }

  // Rule 2: If truck leaves after route (k-1) but before or at route k, use route k duration
  for (let i = 1; i < preparedRoutes.length; i++) {
    const prevRoute = preparedRoutes[i - 1];
    const currRoute = preparedRoutes[i];
    if (
      departureMinutes > prevRoute.departureMinutes &&
      departureMinutes <= currRoute.departureMinutes
    ) {
      return currRoute.durationTravelMinutes;
    }
  }

  // Rule 3: If truck leaves after the last route, use the last route duration
  const lastRoute = preparedRoutes[preparedRoutes.length - 1];
  return lastRoute.durationTravelMinutes;
}

// ----------------------------------------------------------------------------
// 3. FLEET DAY DISCRETE-EVENT SIMULATION
// ----------------------------------------------------------------------------

/**
 * Simulates one standard work day of haulage operations for a fleet of N trucks.
 * 
 * Models:
 * - Single-loader FIFO queue at the loading site.
 * - Staggered initial departures (Truck 1 at start + loadTime, Truck 2 at start + 2*loadTime, etc.).
 * - Unconstrained tipping at the disposal yard.
 * - Dynamic travel times matching time-of-day routes or fixed map duration.
 * - Return travel and potential queueing at the loading site for consecutive runs.
 * - Cutoff at finishTime (runs must finish tipping by finishTime to be counted).
 * 
 * @param {Object} params
 * @param {number} [params.allocatedTrucks=1] Number of trucks allocated
 * @param {string} [params.startTime="07:00"] Shift start time (HH:MM)
 * @param {string} [params.finishTime="17:00"] Shift finish time (HH:MM)
 * @param {number} [params.loadTime=10] Loading time in minutes
 * @param {number} [params.tipTime=10] Tipping time in minutes
 * @param {Array<Object>|null} [params.routes=null] Routes array from /routes/loads
 * @param {number} [params.fallbackTravelMinutes=30] Map duration in minutes
 * @param {number} [params.maxLoads=Infinity] Optional target load limit (for partial day simulation)
 * @returns {Object} Detailed simulation report
 */
export function simulateFleetDay({
  allocatedTrucks = 1,
  startTime = "07:00",
  finishTime = "17:00",
  loadTime = 10,
  tipTime = 10,
  routes = null,
  fallbackTravelMinutes = 30,
  maxLoads = Infinity,
}) {
  const numTrucks = Math.max(1, parseInt(allocatedTrucks, 10) || 1);
  const startMins = parseTimeToMinutes(startTime || "07:00");
  let finishMins = parseTimeToMinutes(finishTime || "17:00");
  if (finishMins <= startMins) {
    finishMins += 24 * 60; // Support overnight window if finish is numerically smaller
  }

  const loadDuration = Math.max(1, Number(loadTime) || 10);
  const tipDuration = Math.max(1, Number(tipTime) || 10);

  // The loading bay availability tracker (only 1 truck can load at a time)
  let loadingBayAvailableAt = startMins;

  // Initialize all trucks at the loading site at the start of the day
  // First leg of every day of every truck will be "load to tip"
  const trucks = [];
  for (let i = 0; i < numTrucks; i++) {
    trucks.push({
      id: i + 1,
      availableAt: startMins, // Ready at loading site at start of day
      completedLoads: 0,
      legs: [],
    });
  }

  let totalCompletedLoads = 0;
  const timelineEvents = [];

  // Continue simulating while remaining within shift window and under maxLoads
  while (totalCompletedLoads < maxLoads) {
    // Pick the truck ready to queue earliest at the loading site (FIFO)
    let selectedTruck = null;
    let earliestAvailable = Infinity;

    for (const t of trucks) {
      if (t.availableAt < earliestAvailable) {
        earliestAvailable = t.availableAt;
        selectedTruck = t;
      }
    }

    // If no truck is available before finishTime, the day is complete
    if (!selectedTruck || earliestAvailable >= finishMins) {
      break;
    }

    // Loading begins once BOTH the truck has arrived AND the loading bay is free
    const startLoadTime = Math.max(selectedTruck.availableAt, loadingBayAvailableAt);

    // If loading cannot even begin before finishTime, stop
    if (startLoadTime >= finishMins) {
      break;
    }

    // Loading takes loadDuration minutes
    const finishLoadTime = startLoadTime + loadDuration;
    loadingBayAvailableAt = finishLoadTime; // Loading bay remains occupied until now

    // Truck leaves loading site
    const departSiteTime = finishLoadTime;

    // Outbound travel to disposal site based on time-of-day departure
    const outboundTravel = getTravelDurationForDeparture(
      departSiteTime,
      "load to tip",
      routes,
      fallbackTravelMinutes
    );
    const arriveTipTime = departSiteTime + outboundTravel;

    // Tipping at disposal yard (no queueing at tip site)
    const finishTipTime = arriveTipTime + tipDuration;

    // If the load cannot complete tipping before shift finishTime, it cannot be completed today
    if (finishTipTime > finishMins) {
      selectedTruck.availableAt = Infinity; // Mark truck done for the day
      continue;
    }

    // Load successfully delivered!
    totalCompletedLoads++;
    selectedTruck.completedLoads++;

    const runRecord = {
      runIndex: selectedTruck.completedLoads,
      truckId: selectedTruck.id,
      arriveSiteTime: selectedTruck.availableAt,
      startLoadTime,
      finishLoadTime,
      departSiteTime,
      outboundTravel,
      arriveTipTime,
      finishTipTime,
      completedAtText: minutesToTimeString(finishTipTime),
      startLoadText: minutesToTimeString(startLoadTime),
      departText: minutesToTimeString(departSiteTime),
      arriveTipText: minutesToTimeString(arriveTipTime),
    };

    // If we have reached our target load limit, record and stop
    if (totalCompletedLoads >= maxLoads) {
      selectedTruck.legs.push(runRecord);
      timelineEvents.push(runRecord);
      break;
    }

    // Inbound return travel from tip site back to loading site
    const inboundTravel = getTravelDurationForDeparture(
      finishTipTime,
      "tip to load",
      routes,
      fallbackTravelMinutes
    );
    const arriveBackSiteTime = finishTipTime + inboundTravel;
    runRecord.inboundTravel = inboundTravel;
    runRecord.arriveBackSiteTime = arriveBackSiteTime;
    runRecord.returnText = minutesToTimeString(arriveBackSiteTime);

    // Update truck availability for its next possible run
    selectedTruck.availableAt = arriveBackSiteTime;
    selectedTruck.legs.push(runRecord);
    timelineEvents.push(runRecord);
  }

  const loadsPerTruck = trucks.map((t) => t.completedLoads);
  const activeTrucks = trucks.filter((t) => t.completedLoads > 0).length;

  return {
    totalLoads: totalCompletedLoads,
    loadsPerTruck,
    activeTrucks,
    trucks,
    timelineEvents,
  };
}

// ----------------------------------------------------------------------------
// 4. HIGH-LEVEL DAYS & CARTAGE COSTS CALCULATOR
// ----------------------------------------------------------------------------

/**
 * Main calculation engine for haulage quotes and packages.
 * 
 * Computes:
 * - Daily capacity of the fleet considering queueing & traffic.
 * - Total days required to move the job tonnage.
 * - Billed days (split into full days and half/full remainder days).
 * - Base cartage costs for the fleet.
 * - Single-truck baseline comparison days.
 * 
 * @param {Object} options
 * @param {number} [options.allocatedTrucks=1] Number of trucks
 * @param {number} [options.totalLoads] Total loads for the job
 * @param {number} [options.totalTonnes] Total material tonnes (fallback if totalLoads missing)
 * @param {number} [options.truckCapacity] Truck capacity in tonnes
 * @param {number} [options.dayRate=1500] Truck day rate in dollars
 * @param {string} [options.startTime="07:00"] Shift start (HH:MM)
 * @param {string} [options.finishTime="17:00"] Shift finish (HH:MM)
 * @param {number} [options.loadTime=10] Loading time in minutes
 * @param {number} [options.tipTime=10] Tipping time in minutes
 * @param {Array<Object>|null} [options.routes=null] Routes from /routes/loads
 * @param {number} [options.mapDurationMinutes=30] Map duration in minutes
 * @param {boolean} [options.isEstimate=false] True if added without clicking "Calculate Haulage"
 * @param {number} [options.singleTruckDays] Optional pre-calculated 1-truck days
 * @param {number} [options.dailyRuns1Truck] Optional pre-calculated 1-truck daily runs
 * @returns {Object} Complete calculation results
 */
export function calculateFleetDaysAndCosts(options = {}) {
  const allocatedTrucks = Math.max(
    1,
    parseInt(options.allocatedTrucks, 10) || 1
  );
  const dayRate = Math.max(0, Number(options.dayRate) || 0);
  const truckCapacity = Math.max(1, Number(options.truckCapacity) || 1);
  const totalTonnes = Math.max(0, Number(options.totalTonnes) || 0);

  const startTime = options.startTime || "07:00";
  const finishTime = options.finishTime || "17:00";
  const loadTime = Number(options.loadTime) != null ? Number(options.loadTime) : 10;
  const tipTime = Number(options.tipTime) != null ? Number(options.tipTime) : 10;

  // If user did NOT click "Calculate Haulage", routes must not be used (use map duration)
  const isEstimate = options.isEstimate === true;
  const routes = isEstimate ? null : (options.routes || null);

  const mapDurationMinutes = Math.max(
    1,
    Math.round(Number(options.mapDurationMinutes) || 30)
  );

  // 1. Simulate 1-truck baseline for comparison
  let dailyRuns1Truck = options.dailyRuns1Truck;
  if (dailyRuns1Truck == null || dailyRuns1Truck <= 0) {
    const sim1 = simulateFleetDay({
      allocatedTrucks: 1,
      startTime,
      finishTime,
      loadTime,
      tipTime,
      routes,
      fallbackTravelMinutes: mapDurationMinutes,
    });
    dailyRuns1Truck = sim1.totalLoads;
  }

  // 2. Simulate full day for allocated fleet (N trucks)
  const fleetDaySim = simulateFleetDay({
    allocatedTrucks,
    startTime,
    finishTime,
    loadTime,
    tipTime,
    routes,
    fallbackTravelMinutes: mapDurationMinutes,
  });

  const dailyFleetLoads = fleetDaySim.totalLoads;
  const dailyRunsPerTruck =
    allocatedTrucks > 0 ? dailyFleetLoads / allocatedTrucks : 0;

  // 3. Resolve total loads required
  // If total material tonnes and truck capacity are provided, physical loads
  // required to move that volume is unconditionally Math.ceil(totalTonnes / truckCapacity).
  let resolvedTotalLoads = 0;
  if (totalTonnes > 0 && truckCapacity > 0) {
    resolvedTotalLoads = Math.ceil(totalTonnes / truckCapacity);
  } else if (options.totalLoads != null && Number(options.totalLoads) > 0) {
    resolvedTotalLoads = Number(options.totalLoads);
  } else {
    resolvedTotalLoads = dailyFleetLoads || dailyRuns1Truck || 1;
  }

  // 4. Calculate total days for 1 truck baseline
  let totalDays1Truck = 0;
  if (dailyRuns1Truck > 0 && resolvedTotalLoads > 0) {
    totalDays1Truck = resolvedTotalLoads / dailyRuns1Truck;
  } else if (options.singleTruckDays != null && Number(options.singleTruckDays) > 0) {
    totalDays1Truck = Number(options.singleTruckDays);
  }

  // 5. Calculate total days for the allocated fleet
  let calculatedDays = 0;
  if (dailyFleetLoads > 0 && resolvedTotalLoads > 0) {
    calculatedDays = resolvedTotalLoads / dailyFleetLoads;
  } else if (totalDays1Truck > 0 && allocatedTrucks > 0) {
    calculatedDays = totalDays1Truck / allocatedTrucks;
  }

  // 6. Split into full and remainder billing days
  const split = getFullAndRemainderDays(calculatedDays);
  const fullDays = split.fullDays;
  const remainderDays = split.remainderDays;
  const billedDays = split.billedDays;

  // 7. Remainder day simulation (to find which trucks actually work on the partial day)
  const fullDaysDeliveredLoads = fullDays * dailyFleetLoads;
  const remainingLoadsOnLastDay = Math.max(0, resolvedTotalLoads - fullDaysDeliveredLoads);

  let remainderTrucksCount = 0;
  let remainderSim = null;
  if (remainderDays > 0 && remainingLoadsOnLastDay > 0) {
    remainderSim = simulateFleetDay({
      allocatedTrucks,
      startTime,
      finishTime,
      loadTime,
      tipTime,
      routes,
      fallbackTravelMinutes: mapDurationMinutes,
      maxLoads: remainingLoadsOnLastDay,
    });
    remainderTrucksCount = remainderSim.activeTrucks || 1;
  } else if (remainderDays > 0) {
    remainderTrucksCount = 1;
  }

  // 8. Calculate Base Cartage Cost
  // Full days cost: all allocated trucks operate for the full day
  const fullDaysCost = allocatedTrucks * fullDays * dayRate;

  // Remainder day cost:
  // - If fullDays === 0 and remainderDays === 0.5 (entire job is completed in a half-day):
  //   all allocated trucks worked on that half day -> allocatedTrucks * remainderDays * dayRate.
  // - If fullDays > 0 and remainderDays === 0.5 (job has full days + remainder half-day):
  //   the leftover loads on the partial day only need 1 truck (or leftover trucks) -> dayRate * remainderDays.
  const remainderMultiplier = fullDays === 0 ? allocatedTrucks : 1;
  const remainderCost = remainderMultiplier * remainderDays * dayRate;
  const fleetRemainderCost = fullDaysCost + (allocatedTrucks * remainderDays * dayRate);
  const exactUtilizedCost = fullDaysCost + (remainderTrucksCount * remainderDays * dayRate);

  const baseCartageCost = fullDaysCost + remainderCost;

  return {
    allocatedTrucks,
    totalLoads: resolvedTotalLoads,
    dailyFleetLoads,
    dailyRunsPerTruck,
    loadsPerTruck: fleetDaySim.loadsPerTruck,
    calculatedDays,
    fullDays,
    remainderDays,
    billedDays,
    totalDays1Truck,
    dailyRuns1Truck,
    dayRate,
    baseCartageCost,
    fullDaysCost,
    fleetRemainderCost,
    exactUtilizedCost,
    remainderTrucksCount,
    fleetDaySim,
    remainderSim,
  };
}

/**
 * Convenience helper to calculate metrics directly from a CustomerQuote haulagePackage.
 * 
 * @param {Object} pkg Haulage package object
 * @param {Object} [overrides={}] Optional overrides (e.g. override allocatedTrucks or dayRate)
 * @param {Object} [routeEstimatesMap={}] Map of route estimates by yardId
 * @returns {Object|null}
 */
export function calculatePackageFleetMetrics(pkg, overrides = {}, routeEstimatesMap = {}) {
  if (!pkg) return null;

  const allocatedTrucks = overrides.allocatedTrucks != null
    ? overrides.allocatedTrucks
    : Math.max(1, parseInt(pkg.allocatedTrucks, 10) || 1);

  const dayRate = overrides.dayRate != null ? overrides.dayRate : Number(pkg.dayRate || 0);
  const truckCapacity = Number(pkg.totalCapacity || 1);
  const dailyRuns = Number(pkg.runs || 0);
  const tonnes = Number(
    pkg.totalTonnes || pkg.tonnesDeliveredInDay || (truckCapacity * dailyRuns) || 0
  );

  const totalLoads = overrides.totalLoads != null
    ? overrides.totalLoads
    : (pkg.totalLoads != null && pkg.totalLoads > 0
        ? Number(pkg.totalLoads)
        : (tonnes > 0 && truckCapacity > 0 ? Math.ceil(tonnes / truckCapacity) : (dailyRuns || 0)));

  const mapDuration = pkg.travelMins != null
    ? pkg.travelMins
    : (routeEstimatesMap && pkg.yardId && routeEstimatesMap[pkg.yardId]
        ? routeEstimatesMap[pkg.yardId].durationMinutes
        : 30);

  const isEstimate = pkg.isEstimate ?? (!pkg.load || !pkg.load.routes || pkg.load.routes.length === 0);

  return calculateFleetDaysAndCosts({
    allocatedTrucks,
    totalLoads,
    totalTonnes: tonnes,
    truckCapacity,
    dayRate,
    startTime: pkg.startTime || "07:00",
    finishTime: pkg.finishTime || "17:00",
    loadTime: pkg.loadTime != null ? pkg.loadTime : 10,
    tipTime: pkg.tipTime != null ? pkg.tipTime : 10,
    routes: pkg.load?.routes || null,
    mapDurationMinutes: mapDuration,
    isEstimate,
    singleTruckDays: Number(pkg.totalDays || 0),
    dailyRuns1Truck: dailyRuns,
  });
}
